/**
 * Upload LOR Review Mode Modal
 *
 * Modal that appears when user clicks "Upload my own LOR" to let them choose:
 * - A specific template for field-based review
 * - Or let AI auto-detect fields to review
 */
import { FileText, Sparkles } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import type { TemplateLite } from '../types';

type ReviewMode = 'ai' | 'template';

interface UploadLorReviewModeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templates: TemplateLite[];
  isLoadingTemplates?: boolean;
  onConfirm: (templateId: string | null) => void;
  isUploading?: boolean;
}

/**
 * UploadLorReviewModeModal
 *
 * Lets the user choose how FinchGPT should review their uploaded LOR:
 * - AI Mode: Dynamically identifies and reviews relevant fields
 * - Template Mode: Uses a specific template's field definitions
 */
export const UploadLorReviewModeModal: React.FC<
  UploadLorReviewModeModalProps
> = ({
  open,
  onOpenChange,
  templates,
  isLoadingTemplates = false,
  onConfirm,
  isUploading = false,
}) => {
  const [reviewMode, setReviewMode] = useState<ReviewMode>('ai');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );

  const handleConfirm = () => {
    if (reviewMode === 'ai') {
      onConfirm(null);
    } else {
      onConfirm(selectedTemplateId);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isUploading) {
      onOpenChange(nextOpen);
      // Reset state when closing
      if (!nextOpen) {
        setReviewMode('ai');
        setSelectedTemplateId(null);
      }
    }
  };

  const canConfirm =
    reviewMode === 'ai' || (reviewMode === 'template' && selectedTemplateId);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle>How should FinchGPT review your LOR?</DialogTitle>
          <DialogDescription>
            Choose how the AI should identify fields to review in your uploaded
            Letter of Representation.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={reviewMode}
            onValueChange={(value) => setReviewMode(value as ReviewMode)}
            className="space-y-4"
          >
            {/* AI Auto-detect option */}
            <div
              role="button"
              tabIndex={0}
              className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                reviewMode === 'ai'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setReviewMode('ai')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setReviewMode('ai');
                }
              }}
            >
              <RadioGroupItem value="ai" id="mode-ai" className="mt-1" />
              <div className="flex-1">
                <Label
                  htmlFor="mode-ai"
                  className="flex items-center gap-2 cursor-pointer font-medium"
                >
                  <Sparkles className="h-4 w-4 text-blue-500" />
                  Let AI choose fields
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  FinchGPT will analyze your LOR and automatically identify the
                  most important fields to review (e.g., policy number, claim
                  number, adjuster name, dates).
                </p>
              </div>
            </div>

            {/* Template-based option */}
            <div
              role="button"
              tabIndex={0}
              className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                reviewMode === 'template'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setReviewMode('template')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setReviewMode('template');
                }
              }}
            >
              <RadioGroupItem
                value="template"
                id="mode-template"
                className="mt-1"
              />
              <div className="flex-1">
                <Label
                  htmlFor="mode-template"
                  className="flex items-center gap-2 cursor-pointer font-medium"
                >
                  <FileText className="h-4 w-4 text-green-600" />
                  Use template fields
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Review using a specific template&apos;s field definitions.
                  Useful when your LOR matches one of your firm&apos;s
                  templates.
                </p>

                {reviewMode === 'template' && (
                  <div className="mt-3">
                    <Select
                      value={selectedTemplateId ?? undefined}
                      onValueChange={setSelectedTemplateId}
                      disabled={isLoadingTemplates || templates.length === 0}
                    >
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue
                          placeholder={
                            isLoadingTemplates
                              ? 'Loading templates…'
                              : templates.length === 0
                                ? 'No templates available'
                                : 'Select a template'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-60">
                        {templates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {templates.length === 0 && !isLoadingTemplates && (
                      <p className="text-xs text-amber-600 mt-2">
                        No templates available for this coverage. Consider using
                        AI mode instead.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm || isUploading}>
            {isUploading ? 'Uploading…' : 'Upload & Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
